/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// Extracted from original/src/components/CashTransfers.tsx:2207-2340 — the A4 transfer voucher ("وصل مالي رسمي", the
// `receipt-payment-zone-<id>` element the old details window showed and `printElement` printed; reference
// documentation/01_visual_reference/print/p08_transfer_voucher-1.png). ADR-08 §2.1 "copy, then cut logic".
// Kept: every class string of the zone (fixed light colours also in dark mode, as the old card), the header block, the
// two-column attribute grid, the partner / customer highlight boxes, the amounts breakdown with the total box and the
// settlement box, the operator / timestamp footer.
// Cut: the hard-coded company name "شركة الفيلسوف للصيرفة" and badge text (F-130) → `officeName` (D-052, D-125); the
// receipt number `#REC-<array id>` → the server's gapless voucher number (D-147); `debts.find(...)` name lookups →
// `partnerName` / `customerName`; `formatRemittanceNumber` / `getCurrencySymbol` → preformatted labels;
// `new Date(created_at).toLocaleString(browser locale)` → `dateLabel` in the office time zone (D-064, D-162).
// Changed by decision: an outgoing total through a partner includes the partner fee (D-081); on an incoming transfer
// through a partner the fee line says it is an extra the partner pays us (D-083) and a line shows what the beneficiary
// was paid (D-080 / D-082); a reprint carries "نسخة / Copy" (D-147). All strings are `print.voucher.*` keys.
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { PrintOffice } from './StatementA4.js';

export interface TransferVoucherA4Props {
  readonly locale: Locale;
  /** Root element id (the print helper prints by id). */
  readonly id?: string;
  readonly officeName: string;
  /** Server voucher number (D-147); null while held for approval. */
  readonly voucherNumber: string | null;
  readonly copy: boolean;
  readonly code: string;
  readonly beneficiaryName: string;
  readonly documentId: string;
  readonly direction: 'sent' | 'received';
  readonly destination: string;
  readonly route: 'vault' | 'office';
  readonly paymentLabel: string;
  readonly partnerName: string | null;
  readonly customer: { readonly name: string; readonly kind: 'credit' | 'trust' } | null;
  readonly principalLabel: string;
  /** [C35] null when the office hides profit and commission on customer-facing documents (D-148). */
  readonly commissionLabel: string | null;
  /** Present on the office route. */
  readonly partnerFeeLabel: string | null;
  /** Incoming through a partner: the fee is the partner's extra to us (D-083). */
  readonly partnerFeeIncoming: boolean;
  readonly totalLabel: string;
  /** Incoming: what the beneficiary was paid (D-080 / D-082); null on outgoing. */
  readonly paidToBeneficiaryLabel: string | null;
  readonly settlement: { readonly cashLabel: string; readonly rateLabel: string | null } | null;
  readonly operatorName: string;
  readonly dateLabel: string;
  /** [C35] D-148 words of the total (server); printed on the 80 mm copy only — the old A4 voucher had no words line. */
  readonly words?: readonly string[];
  /** [C35] office identity for the 80 mm header (the A4 voucher prints the name only, as the old one). */
  readonly office?: PrintOffice;
}

export function TransferVoucherA4(p: TransferVoucherA4Props) {
  const t = createT(p.locale);
  return (
    <div
      id={p.id ?? 'transfer-voucher-template'}
      className="p-8 border-2 border-slate-200 dark:border-[#30363d] rounded-[2rem] bg-white text-slate-800 text-right space-y-6 printable-report"
      dir={p.locale === 'ar' ? 'rtl' : 'ltr'}
      data-testid="transfer-voucher"
    >
      {/* Header branding */}
      <div className="text-center pb-5 border-b-2 border-dashed border-gray-200 space-y-1">
        <h3 className="text-2xl font-black text-indigo-700 tracking-wider">{p.officeName}</h3>
        <p className="text-[9px] text-gray-400 font-mono tracking-widest uppercase">{t('print.voucher.platform_line')}</p>
        <span className="inline-block mt-2 px-4 py-1 bg-indigo-50 text-indigo-700 font-black rounded-full text-[10px]">
          {t('print.voucher.receipt_title', { office: p.officeName })}
        </span>
        {p.copy && (
          <span className="inline-block mt-2 mx-2 px-4 py-1 bg-rose-50 text-rose-700 font-black rounded-full text-[10px]" data-testid="transfer-voucher-copy">
            {t('print.voucher.copy')}
          </span>
        )}
      </div>

      {/* Grid attributes info */}
      <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-[11px] leading-relaxed">
        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.receipt_number')}</span>
          <span className="font-mono text-xs font-black">{p.voucherNumber !== null ? `#REC-${p.voucherNumber}` : '—'}</span>
        </div>
        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.transfer_code')} :</span>
          <span className="font-mono text-xs font-black text-indigo-600">{p.code}</span>
        </div>

        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.client_name')} :</span>
          <span className="text-xs font-black text-slate-900">{p.beneficiaryName}</span>
        </div>
        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.client_doc')} :</span>
          <span className="text-xs font-bold text-slate-700">{p.documentId}</span>
        </div>

        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.transfer_type')} :</span>
          <span className="text-xs font-extrabold text-slate-900">
            {p.direction === 'sent' ? t('print.voucher.type_out') : t('print.voucher.type_in')}
          </span>
        </div>
        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.destination')} :</span>
          <span className="text-xs font-black text-slate-900">📍 {p.destination}</span>
        </div>

        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.source')}</span>
          <span className="text-xs font-bold text-slate-800">
            {p.route === 'vault' ? t('print.voucher.source_vault') : `${t('print.voucher.tab_office')} - ${p.partnerName ?? ''}`}
          </span>
        </div>
        <div>
          <span className="text-gray-400 font-bold block">{t('print.voucher.payment_type')} :</span>
          <span className="text-xs font-extrabold text-slate-900">{p.paymentLabel}</span>
        </div>

        {p.partnerName !== null && (
          <div className="col-span-2 sm:col-span-1 bg-amber-50/60 p-2.5 rounded-xl border border-amber-100/75">
            <span className="text-amber-700 font-extrabold block text-[10px] mb-0.5">{t('print.voucher.partner_name')}</span>
            <span className="text-xs font-black text-amber-900">{p.partnerName}</span>
          </div>
        )}

        {p.customer !== null && (
          <div className="col-span-2 sm:col-span-1 bg-indigo-50/60 p-2.5 rounded-xl border border-indigo-150/60">
            <span className="text-indigo-700 font-extrabold block text-[10px] mb-0.5">
              {p.customer.kind === 'credit' ? t('print.voucher.client_credit') : t('print.voucher.client_trust')}
            </span>
            <span className="text-xs font-black text-indigo-900">{p.customer.name}</span>
          </div>
        )}

        {/* Pricing and fee calculations breakdown */}
        <div className="col-span-2 pt-4 border-t border-gray-100 text-xs space-y-2">
          <div className="flex justify-between font-bold text-gray-500">
            <span>{t('print.voucher.principal')}</span>
            <span className="font-mono">{p.principalLabel}</span>
          </div>
          {p.commissionLabel !== null && (
            <div className="flex justify-between font-bold text-emerald-600">
              <span>{t('print.voucher.commission')}</span>
              <span className="font-mono text-emerald-600">+{p.commissionLabel}</span>
            </div>
          )}
          {p.partnerFeeLabel !== null && (
            <div className="flex justify-between font-bold text-amber-600">
              <span>{p.partnerFeeIncoming ? t('print.voucher.partner_fee_in') : t('print.voucher.partner_fee')}</span>
              <span className="font-mono text-amber-600">+{p.partnerFeeLabel}</span>
            </div>
          )}

          <div className="flex justify-between items-center bg-indigo-50/50 p-4 rounded-xl mt-2 border border-indigo-100">
            <span className="text-xs font-black text-indigo-900">{t('print.voucher.total')}</span>
            <span className="text-lg font-black font-mono text-indigo-700" data-testid="transfer-voucher-total">
              {p.totalLabel}
            </span>
          </div>

          {p.paidToBeneficiaryLabel !== null && (
            <div className="flex justify-between font-bold text-slate-700">
              <span>{t('print.voucher.paid_to_beneficiary')}</span>
              <span className="font-mono">{p.paidToBeneficiaryLabel}</span>
            </div>
          )}

          {p.settlement !== null && (
            <div className="bg-emerald-50/80 p-4 rounded-xl border border-emerald-200 text-xs space-y-1.5 mt-2">
              <div className="flex justify-between items-center text-emerald-900 font-extrabold">
                <span>{t('print.voucher.settlement_cash')}</span>
                <span className="font-mono text-base font-black text-emerald-700">
                  {p.settlement.cashLabel}
                </span>
              </div>
              {p.settlement.rateLabel !== null && (
                <div className="flex justify-between items-center text-slate-500 text-[10px]">
                  <span>{t('print.voucher.settlement_rate')}</span>
                  <span className="font-mono font-bold">
                    {p.settlement.rateLabel}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Signed operators info */}
      <div className="pt-4 border-t border-dashed border-gray-200 flex justify-between items-end text-[9px] text-gray-400">
        <div>
          <span className="block">{t('print.voucher.operator')}:</span>
          <span className="font-bold text-gray-600">{p.operatorName}</span>
        </div>
        <div>
          <span className="block">{t('print.voucher.date')}:</span>
          <span className="font-mono text-gray-500">{p.dateLabel}</span>
        </div>
      </div>
    </div>
  );
}
